import { AppLink, MenuItem, MenuLabel, MenuSeparator } from "@amber/ui";
import type { CommunicationView, CopyView, EvidenceRecord, MatchProposalView } from "../../contracts/index";
import { clock } from "../format";

const ALIGNMENT_LABELS: Record<CopyView["alignment"], string> = {
  aligned: "aligned",
  uncertain: "alignment uncertain",
  none: "no alignment recorded",
  self: "current",
};
export const rangeLabel = (r: readonly [number, number] | null) => (r ? `${clock(r[0])}–${clock(r[1])}` : "whole item");

export type CopyAction =
  | { kind: "add"; communication: CommunicationView }
  | { kind: "primary"; communication: CommunicationView; copy: CopyView }
  | { kind: "separate"; communication: CommunicationView; copy: CopyView }
  | { kind: "match"; communication: CommunicationView; proposal: MatchProposalView; decision: "same" | "different" };

export function copiesButtonLabel(record: EvidenceRecord): string {
  const coms = record.communications;
  const others = coms.reduce((n, c) => n + c.copies.others, 0);
  const possible = coms.reduce((n, c) => n + c.copies.proposals.length, 0);
  const matches = possible > 0 ? ` · ${String(possible)} possible match${possible === 1 ? "" : "es"}` : "";
  if (coms.length > 0 && coms.every((c) => c.copies.role === "variant"))
    return `Copy of ${coms[0]?.copies.primary_title ?? "the primary"}${matches}`;
  const copy = coms.find((c) => c.copies.role === "variant");
  if (copy)
    return `Communication ${String(copy.ordinal)} is a copy of ${copy.copies.primary_title ?? "the primary"}${matches}`;
  if (others === 0) return `Primary copy${matches}`;
  return `Primary copy · ${String(others)} other cop${others === 1 ? "y" : "ies"}${matches}`;
}

export function CopiesMenuItems({ record, onAction }: { record: EvidenceRecord; onAction: (a: CopyAction) => void }) {
  const can = record.actions.copies;
  const many = record.communications.length > 1;
  return (
    <>
      {record.communications.map((c, i) => (
        <div key={c.id}>
          {i > 0 ? <MenuSeparator /> : null}
          <MenuLabel>
            {many
              ? `${c.title ?? `Communication ${String(c.ordinal)}`} · ${rangeLabel(c.range_ms)}`
              : "Copies of this communication"}
          </MenuLabel>
          {c.copies.list.map((copy) => (
            <MenuItem key={copy.id} asChild={!copy.current}>
              {copy.current ? (
                <span className="block w-full">
                  <CopyLine copy={copy} />
                </span>
              ) : (
                <AppLink href={`/items/${copy.item_id}`} className="block w-full">
                  <CopyLine copy={copy} />
                </AppLink>
              )}
            </MenuItem>
          ))}
          {c.copies.list.length <= 1 ? <MenuItem disabled>No other copies are known</MenuItem> : null}
          {c.copies.proposals.length > 0 ? (
            <>
              <MenuSeparator />
              <MenuLabel>Possible matches · not linked yet</MenuLabel>
              {c.copies.proposals.map((p) => (
                <div key={p.id}>
                  <MenuItem asChild>
                    <AppLink href={`/items/${p.other_item_id}`} className="block w-full">
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-strong">{p.other_title}</span>
                        <span className="block truncate text-md text-muted">
                          {p.relationship_label} · {String(p.words)} shared words · {p.dates}
                        </span>
                      </span>
                    </AppLink>
                  </MenuItem>
                  {p.can_decide.enabled ? (
                    <>
                      <MenuItem
                        onSelect={() => {
                          onAction({ kind: "match", communication: c, proposal: p, decision: "same" });
                        }}
                      >
                        <span className="min-w-0">
                          Same communication… <span className="text-muted">{p.other_title}</span>
                        </span>
                      </MenuItem>
                      <MenuItem
                        onSelect={() => {
                          onAction({ kind: "match", communication: c, proposal: p, decision: "different" });
                        }}
                      >
                        <span className="min-w-0">
                          Different communications… <span className="text-muted">{p.other_title}</span>
                        </span>
                      </MenuItem>
                    </>
                  ) : null}
                </div>
              ))}
            </>
          ) : null}
          {c.copies.list.some((copy) => copy.can_make_primary.enabled || copy.can_separate.enabled) ? (
            <MenuSeparator />
          ) : null}
          {c.copies.list
            .filter((copy) => copy.can_make_primary.enabled)
            .map((copy) => (
              <MenuItem
                key={`p-${copy.id}`}
                onSelect={() => {
                  onAction({ kind: "primary", communication: c, copy });
                }}
              >
                <span className="min-w-0">
                  Make primary… <span className="text-muted">{copy.title}</span>
                </span>
              </MenuItem>
            ))}
          {c.copies.list
            .filter((copy) => copy.can_separate.enabled)
            .map((copy) => (
              <MenuItem
                key={`s-${copy.id}`}
                onSelect={() => {
                  onAction({ kind: "separate", communication: c, copy });
                }}
              >
                <span className="min-w-0">
                  Not the same communication… <span className="text-muted">{copy.title}</span>
                </span>
              </MenuItem>
            ))}
          <MenuItem
            disabled={!can.enabled || record.pinned !== null}
            onSelect={() => {
              onAction({ kind: "add", communication: c });
            }}
            title={can.enabled ? undefined : can.reason}
          >
            Add a copy…
          </MenuItem>
        </div>
      ))}
      {!can.enabled ? (
        <MenuLabel className="max-w-[300px] font-normal whitespace-normal">{can.reason}</MenuLabel>
      ) : null}
    </>
  );
}

function CopyLine({ copy }: { copy: CopyView }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="flex items-center gap-1.5">
        <span className="min-w-0 flex-1 truncate font-strong">{copy.title}</span>
        {copy.role === "primary" ? (
          <span className="flex-none rounded-full border border-border px-1.5 text-sm font-strong">Primary</span>
        ) : null}
        {copy.current ? (
          <span className="flex-none rounded-full border border-divider px-1.5 text-sm text-muted">Current</span>
        ) : null}
      </span>
      <span className="block truncate text-md text-muted">
        {copy.relationship_label} · {rangeLabel(copy.range_ms)}
        {copy.current ? "" : ` · ${ALIGNMENT_LABELS[copy.alignment]}`} · {copy.status}
      </span>
    </span>
  );
}
